import React, { useState } from 'react';
import { Bell, Send, Check, AlertTriangle, ChevronDown, ChevronUp, Loader2, Image as ImageIcon, Database } from 'lucide-react';
import { toast } from 'react-toastify';
import { LysepAPI } from '../../../contexts/ApiContext';

const NotificationSender = ({ users, onExpandChange }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [form, setForm] = useState({
        sendType: 'user', // user, all, tokens
        userId: '',
        tokens: '',
        title: '',
        body: '',
        imageUrl: '',
        data: ''
    });

    const handleExpand = () => {
        const newState = !isExpanded;
        setIsExpanded(newState);
        onExpandChange?.(newState);
    };

    const resetForm = () => {
        setForm({
            sendType: 'user',
            userId: '',
            tokens: '',
            title: '',
            body: '',
            imageUrl: '',
            data: ''
        });
    };

    const handleSend = async (e) => {
        e.preventDefault();

        if (!form.title || !form.body) {
            toast.error('Başlık ve mesaj alanları zorunludur');
            return;
        }

        if (form.sendType === 'user' && !form.userId) {
            toast.error('Kullanıcı seçilmelidir');
            return;
        }

        if (form.sendType === 'tokens' && !form.tokens.trim()) {
            toast.error('En az bir token girilmelidir');
            return;
        }

        if (form.sendType === 'all') {
            if (!window.confirm('Tüm kullanıcılara bildirim göndermek istediğinize emin misiniz?\n\nBu işlem geri alınamaz!')) {
                return;
            }
        }

        setIsSending(true);
        try {
            let response;
            const notificationPayload = {
                notification: {
                    title: form.title,
                    body: form.body,
                    ...(form.imageUrl && { image: form.imageUrl })
                },
                ...(form.data && {
                    data: typeof form.data === 'string' ? JSON.parse(form.data) : form.data
                }),
                ...(form.imageUrl && { imageUrl: form.imageUrl })
            };

            if (form.sendType === 'user') {
                response = await LysepAPI.post('/api/user/push/send-to-user', {
                    userId: form.userId,
                    ...notificationPayload
                });
            } else if (form.sendType === 'all') {
                response = await LysepAPI.post('/api/user/push/send-to-all', notificationPayload);
            } else {
                const tokenArray = form.tokens.split('\n').map(t => t.trim()).filter(t => t);
                response = await LysepAPI.post('/api/user/push/send', {
                    tokens: tokenArray,
                    ...notificationPayload
                });
            }

            if (response.data.state) {
                toast.success(response.data.message);
                // Optional: Reset form or collapse
            } else {
                toast.error(response.data.message || 'Bildirim gönderilemedi');
            }
        } catch (error) {
            console.error('Send notification error:', error);
            toast.error(error.response?.data?.message || 'Bildirim gönderilirken hata oluştu');
        } finally {
            setIsSending(false);
        }
    };

    return (
        <div className="bg-[#1a1a1a] border border-white/10 rounded-xl overflow-hidden shadow-lg transition-all duration-300">
            {/* Header (Accordion Trigger) */}
            <div
                onClick={handleExpand}
                className="flex items-center justify-between p-4 cursor-pointer bg-[#252525] hover:bg-[#2a2a2a] transition-colors"
            >
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-orange-500/10 rounded-lg">
                        <Bell className="w-5 h-5 text-orange-500" />
                    </div>
                    <h3 className="text-lg font-medium text-white">Push Notification Gönder</h3>
                </div>
                <div>
                    {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
                </div>
            </div>

            {/* Content */}
            {isExpanded && (
                <form onSubmit={handleSend} className="p-6 border-t border-white/10 space-y-6 animate-fadeIn">

                    {/* Send Type Select */}
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-400">Gönderim Tipi</label>
                        <select
                            value={form.sendType}
                            onChange={(e) => setForm({ ...form, sendType: e.target.value, userId: '', tokens: '' })}
                            className="w-full bg-[#0a0e13] border border-white/10 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-orange-500 transition-colors appearance-none"
                        >
                            <option value="user">Kullanıcıya Gönder</option>
                            <option value="all">Tüm Kullanıcılara Gönder</option>
                            <option value="tokens">Token'lara Gönder</option>
                        </select>
                        <p className="text-xs text-gray-500">
                            {form.sendType === 'user'
                                ? 'Seçilen kullanıcının tüm aktif cihazlarına bildirim gönderilir'
                                : form.sendType === 'all'
                                    ? 'Sistemdeki tüm aktif cihazlara bildirim gönderilir'
                                    : 'Belirtilen FCM token\'larına bildirim gönderilir (her satıra bir token)'}
                        </p>
                    </div>

                    {/* Dynamic Input based on Type */}
                    {form.sendType === 'user' && (
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-400">Kullanıcı Seç</label>
                            <input
                                list="users-list"
                                value={form.userId}
                                onChange={(e) => {
                                    // Find ID corresponding to input value?
                                    // Datalist input value will be the value of option. 
                                    // If we use ID as value, user sees ID. 
                                    // If we use Name as value, we need to map back to ID.
                                    // Let's try to map back or just use a select if needed.
                                    // Actually, for better UX with datalist, we set value to ID, label to Name?
                                    // No, input takes value.
                                    // Let's implement a simple select for now to avoid ID mapping issues for this iteration.
                                    setForm({ ...form, userId: e.target.value });
                                }}
                                placeholder="Kullanıcı Seç veya ID gir"
                                className="hidden" // Hide this if using Select below
                            />
                            <select
                                value={form.userId}
                                onChange={(e) => setForm({ ...form, userId: e.target.value })}
                                className="w-full bg-[#0a0e13] border border-white/10 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-orange-500 transition-colors appearance-none"
                            >
                                <option value="">Seçiniz</option>
                                {users.map(u => (
                                    <option key={u.id} value={u.id}>
                                        {u.username} {u.email ? `(${u.email})` : ''}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {form.sendType === 'all' && (
                        <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-4 flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 text-orange-500 shrink-0 mt-0.5" />
                            <div>
                                <h4 className="text-sm font-medium text-orange-500">Dikkat</h4>
                                <p className="text-sm text-gray-400 mt-1">
                                    Bu bildirim sistemdeki <strong>tüm aktif cihazlara</strong> gönderilecektir.
                                    Bu işlem geri alınamaz ve tüm kullanıcıları etkileyecektir.
                                </p>
                            </div>
                        </div>
                    )}

                    {form.sendType === 'tokens' && (
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-400">FCM Token'ları</label>
                            <textarea
                                rows={4}
                                value={form.tokens}
                                onChange={(e) => setForm({ ...form, tokens: e.target.value })}
                                className="w-full bg-[#0a0e13] border border-white/10 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-orange-500 transition-colors resize-none font-mono text-xs"
                                placeholder={`Her satıra bir token girin\ntoken1\ntoken2`}
                            />
                        </div>
                    )}

                    <div className="h-px bg-white/10" />

                    {/* Notification Details */}
                    <div className="grid grid-cols-1 gap-4">
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-400">Başlık <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                value={form.title}
                                onChange={(e) => setForm({ ...form, title: e.target.value })}
                                className="w-full bg-[#0a0e13] border border-white/10 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-orange-500 transition-colors"
                                placeholder="Bildirim başlığı"
                            />
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-400">Mesaj <span className="text-red-500">*</span></label>
                            <textarea
                                rows={3}
                                value={form.body}
                                onChange={(e) => setForm({ ...form, body: e.target.value })}
                                className="w-full bg-[#0a0e13] border border-white/10 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-orange-500 transition-colors resize-none"
                                placeholder="Bildirim mesajı"
                            />
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-400 flex items-center gap-2">
                                <ImageIcon className="w-4 h-4" /> Görsel URL (Opsiyonel)
                            </label>
                            <input
                                type="text"
                                value={form.imageUrl}
                                onChange={(e) => setForm({ ...form, imageUrl: e.target.value })}
                                className="w-full bg-[#0a0e13] border border-white/10 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-orange-500 transition-colors"
                                placeholder="https://example.com/image.png"
                            />
                        </div>

                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-400 flex items-center gap-2">
                                <Database className="w-4 h-4" /> Data (Opsiyonel - JSON)
                            </label>
                            <textarea
                                rows={3}
                                value={form.data}
                                onChange={(e) => setForm({ ...form, data: e.target.value })}
                                className="w-full bg-[#0a0e13] border border-white/10 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:border-orange-500 transition-colors resize-none font-mono text-xs"
                                placeholder='{"key": "value", "clickAction": "/dashboard"}'
                            />
                        </div>
                    </div>

                    <div className="flex justify-end pt-2">
                        <button
                            type="submit"
                            disabled={isSending}
                            className="px-6 py-2.5 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors flex items-center gap-2 font-medium disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-orange-500/20"
                        >
                            {isSending ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Gönderiliyor...
                                </>
                            ) : (
                                <>
                                    <Send className="w-4 h-4" />
                                    Gönder
                                </>
                            )}
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
};

export default NotificationSender;
